import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { python } from "@codemirror/lang-python";
import {
  type Language,
  LanguageSupport,
  StreamLanguage,
} from "@codemirror/language";
import { css } from "@codemirror/legacy-modes/mode/css";
import { diff } from "@codemirror/legacy-modes/mode/diff";
import { dockerFile } from "@codemirror/legacy-modes/mode/dockerfile";
import { go } from "@codemirror/legacy-modes/mode/go";
import { http } from "@codemirror/legacy-modes/mode/http";
import { mbox } from "@codemirror/legacy-modes/mode/mbox";
import { properties } from "@codemirror/legacy-modes/mode/properties";
import { ruby } from "@codemirror/legacy-modes/mode/ruby";
import { rust } from "@codemirror/legacy-modes/mode/rust";
import { shell } from "@codemirror/legacy-modes/mode/shell";
import { standardSQL } from "@codemirror/legacy-modes/mode/sql";
import { toml } from "@codemirror/legacy-modes/mode/toml";
import { xml } from "@codemirror/legacy-modes/mode/xml";
import { yaml } from "@codemirror/legacy-modes/mode/yaml";
import { tags } from "@lezer/highlight";

export type CodeLanguageId =
  | "css"
  | "diff"
  | "dockerfile"
  | "eml"
  | "go"
  | "html"
  | "http"
  | "javascript"
  | "json"
  | "jsx"
  | "markdown"
  | "properties"
  | "python"
  | "ruby"
  | "rust"
  | "shell"
  | "sql"
  | "toml"
  | "tsx"
  | "typescript"
  | "yaml";

const factories: Record<CodeLanguageId, () => Language | LanguageSupport> = {
  css: () => StreamLanguage.define(css),
  diff: () => StreamLanguage.define(diff),
  dockerfile: () => StreamLanguage.define(dockerFile),
  // Remap mbox's token names onto the tags our highlight theme already styles.
  eml: () =>
    StreamLanguage.define({
      ...mbox,
      tokenTable: {
        atom: tags.keyword,
        header: tags.string,
        link: tags.string,
      },
    }),
  go: () => StreamLanguage.define(go),
  html: () => StreamLanguage.define(xml),
  http: () => StreamLanguage.define(http),
  javascript: () => javascript(),
  json: () => json(),
  jsx: () => javascript({ jsx: true }),
  markdown: () => markdown(),
  // properties tags values as `quote`, which our theme renders as a comment.
  properties: () =>
    StreamLanguage.define({
      ...properties,
      tokenTable: { quote: tags.string },
    }),
  python: () => python(),
  ruby: () => StreamLanguage.define(ruby),
  rust: () => StreamLanguage.define(rust),
  shell: () => StreamLanguage.define(shell),
  sql: () => StreamLanguage.define(standardSQL),
  toml: () => StreamLanguage.define(toml),
  tsx: () => javascript({ jsx: true, typescript: true }),
  typescript: () => javascript({ typescript: true }),
  yaml: () => StreamLanguage.define(yaml),
};

const aliases: Record<string, CodeLanguageId> = {
  bash: "shell",
  cjs: "javascript",
  conf: "properties",
  dockerfile: "dockerfile",
  env: "properties",
  ini: "properties",
  js: "javascript",
  markdown: "markdown",
  md: "markdown",
  mjs: "javascript",
  mysql: "sql",
  patch: "diff",
  postgres: "sql",
  postgresql: "sql",
  py: "python",
  rb: "ruby",
  rs: "rust",
  sh: "shell",
  sqlite: "sql",
  svg: "html",
  ts: "typescript",
  xml: "html",
  yml: "yaml",
  zsh: "shell",
};

const byExtension: Record<string, CodeLanguageId> = {
  cjs: "javascript",
  conf: "properties",
  css: "css",
  diff: "diff",
  eml: "eml",
  go: "go",
  htm: "html",
  html: "html",
  http: "http",
  ini: "properties",
  js: "javascript",
  json: "json",
  jsx: "jsx",
  less: "css",
  markdown: "markdown",
  md: "markdown",
  mjs: "javascript",
  patch: "diff",
  properties: "properties",
  py: "python",
  rb: "ruby",
  rs: "rust",
  scss: "css",
  sh: "shell",
  sql: "sql",
  svg: "html",
  toml: "toml",
  ts: "typescript",
  tsx: "tsx",
  xml: "html",
  yaml: "yaml",
  yml: "yaml",
  zsh: "shell",
};

const byFilename: Record<string, CodeLanguageId> = {
  ".env": "properties",
  dockerfile: "dockerfile",
};

function resolve(key: string): CodeLanguageId | null {
  if (key in factories) return key as CodeLanguageId;
  return aliases[key] ?? null;
}

export function languageIdForName(name: string): CodeLanguageId | null {
  return resolve(name.toLowerCase());
}

export function languageIdForPath(filePath: string): CodeLanguageId | null {
  const fileName = filePath.slice(filePath.lastIndexOf("/") + 1).toLowerCase();
  const named = byFilename[fileName];
  if (named) return named;
  if (fileName.startsWith(".env.")) return "properties";
  const dot = fileName.lastIndexOf(".");
  if (dot <= 0) return null;
  return byExtension[fileName.slice(dot + 1)] ?? null;
}

const cache = new Map<CodeLanguageId, Language | LanguageSupport>();

function getSupport(id: CodeLanguageId): Language | LanguageSupport {
  const cached = cache.get(id);
  if (cached) return cached;
  const support = factories[id]();
  cache.set(id, support);
  return support;
}

export function getLanguageSupport(
  id: CodeLanguageId
): Language | LanguageSupport {
  return getSupport(id);
}

export function getLanguage(id: CodeLanguageId): Language {
  const support = getSupport(id);
  return support instanceof LanguageSupport ? support.language : support;
}
